import {
    AccommodationTicket,
    BusTicketWithDetails,
    CabanaWithDetails,
    Card,
    CardPerson,
    Ticket,
    VoucherWithDetails,
} from 'store/api-slice/types'
import {
    CartItemTicketDetailDto,
    CartSummaryDto,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import { VOUCHER_TYPE_AMOUNT } from 'constants/app'
import { SpaWithDetails } from 'routes/Upsell/SpaCreator/types/spa'

export type DataLayerEvent = Record<
    string,
    undefined | boolean | string | number | object | object[]
>

declare global {
    interface Window {
        dataLayer?: DataLayerEvent[]
    }
}

type EcommerceItemType = {
    item_name: string
    item_brand: string
    item_variant: string
    price: number
    discount: number
    quantity: number
}

type EcommerceItemsType = EcommerceItemType[]

// TODO: temporary solutions,
// eventually we will get all product names for analytics from cart api
// <--
const VOUCHER_CARD_PERSON_TRANSCRIPTION: {
    [P in CardPerson]: { pl: string; en: string; uk: string }
} = {
    adult: { pl: 'Dorosły', en: 'Adult', uk: 'Adult' },
    teen_reduced: {
        pl: 'do 16 lat',
        en: 'up to 16 years old',
        uk: 'up to 16 years old',
    },
    kid_to_120_cm: { pl: 'do 120 cm', en: 'up to 120 cm', uk: 'up to 120 cm' },
    discounted: { pl: 'Z ulgą', en: 'Reduced', uk: 'Reduced' },
}

const getPersonFromVoucherDescription = (
    description?: string
): string | undefined => {
    if (!description) return undefined

    const personIndex = Object.values(
        VOUCHER_CARD_PERSON_TRANSCRIPTION
    ).findIndex((personDescriptions) =>
        Object.values(personDescriptions).includes(description)
    )

    return personIndex !== -1
        ? Object.keys(VOUCHER_CARD_PERSON_TRANSCRIPTION)[personIndex]
        : undefined
}

const getVariantNameForUnlimitedRenewal = (
    name?: string
): string | undefined => {
    if (!name) return undefined

    const plusVariant = name.toLowerCase().includes('unlimited+')
        ? 'Unlimited+'
        : 'Unlimited'
    const editionVariant = name.toLowerCase().includes('red') ? 'red' : 'blue'

    return `${plusVariant} ${editionVariant}`
}
// -->

/**
 * Pushes events to data layer
 * @param {...Object} dataLayerEvents - parameterized event/events for data layer
 */
export const pushDataLayer = (...dataLayerEvents: DataLayerEvent[]) => {
    window.dataLayer = window.dataLayer || []

    dataLayerEvents.forEach((dataLayerEvent) => {
        window.dataLayer && window.dataLayer.push(dataLayerEvent)
    })
}

/**
 * Transforms prices from multiplied to denominated, float format
 *
 * @param {number} value - price in inner, multiplied by 100 format
 * @return {number} - price in float format, divided by 100
 */
export const parsePriceToFloat = (value: number) => {
    const price = value / 100
    const formattedPrice = price.toFixed(price % 1 > 0 ? 2 : 0)

    return parseFloat(formattedPrice)
}

// TODO: in general, reconsider using hooks and memoizing some of the analytics functions

export const calculateTicketsAddToCartEvent = (
    tickets: (Ticket | GetCartItemPresenter)[]
) => {
    const namesDict = {
        zones: {
            jamango: 'Jamango',
            relax: 'Jamango + Relax',
            saunaria: 'J+R+S',
        },
        persons: {
            adult: 'Adult',
            kid_to_90_cm: 'Kids 0-3',
            kid_to_120_cm: 'Kids 120cm',
            teen_reduced: 'reduced',
            teenager: 'Adult (16-18)',
            senior: 'Seniors',
            pregnant: 'pregnant',
            disabled: 'disabled',
        },
        time: {
            '2h': '2h',
            '4h': '4h',
            day: 'Daypass',
        },
    }
    const FAMILY_BUNDLE_NAME = 'Family Package 2+2'
    const SENIOR_BUNDLE_NAME = 'Senior Offer'

    const ticketsEcommerceItems = tickets.map(
        ({
            productType,
            quantity,
            unitPrice,
            totalPrice,
            detail,
            itemsBundle,
            name,
        }) => {
            const { person, zone, ticketTime } = (detail ||
                {}) as CartItemTicketDetailDto

            const unitPriceWithDiscount = (totalPrice || 0) / (quantity || 1)
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount
            let variantName = ''
            if (productType === 'ticket') {
                if (
                    itemsBundle?.length &&
                    // TODO: figure out with backend how to add meningful key ids to different bundles
                    (name?.toLowerCase().includes('senior') ||
                        name?.toLowerCase().includes('сеньора'))
                )
                    variantName = SENIOR_BUNDLE_NAME
                else
                    variantName = `${namesDict.zones[zone]}, ${namesDict.persons[person]}, ${namesDict.time[ticketTime]}`
            } else variantName = FAMILY_BUNDLE_NAME

            return {
                item_name: 'Ticket',
                item_brand: 'PoP',
                item_variant: variantName,
                price: parsePriceToFloat(unitPriceWithDiscount),
                discount: parsePriceToFloat(unitDiscount),
                quantity: quantity || 1,
            }
        }
    )

    return {
        event: 'productAddToCart',
        ecommerce: {
            currency: 'PLN',
            items: [...ticketsEcommerceItems],
        },
    }
}

export const calculateUpsellsAddToCartEvent = (
    spa: SpaWithDetails[],
    busTickets: BusTicketWithDetails[],
    cabanas: CabanaWithDetails[]
) => {
    const spaEcommerceItems: EcommerceItemsType = spa.map(
        ({ quantity, unitPrice, totalPrice, name }) => {
            const unitPriceWithDiscount = (totalPrice || 0) / (quantity || 1)
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount

            return {
                item_name: 'Wellness & SPA',
                item_brand: 'PoP',
                item_variant: name || '',
                price: parsePriceToFloat(unitPriceWithDiscount),
                discount: parsePriceToFloat(unitDiscount),
                quantity: quantity || 1,
            }
        }
    )

    const busTicketsEcommerceItems: EcommerceItemsType = busTickets.map(
        ({ quantity, unitPrice, totalPrice, name }) => {
            const unitPriceWithDiscount = (totalPrice || 0) / (quantity || 1)
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount
            const variantName = name?.replace(/\s/g, '')

            return {
                item_name: 'Bus',
                item_brand: 'PoP',
                item_variant: variantName || '',
                price: parsePriceToFloat(unitPriceWithDiscount),
                discount: parsePriceToFloat(unitDiscount),
                quantity: quantity || 1,
            }
        }
    )

    const cabanasEcommerceItems: EcommerceItemsType = cabanas.map(
        ({ quantity, unitPrice, totalPrice }) => {
            const unitPriceWithDiscount = (totalPrice || 0) / (quantity || 1)
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount

            return {
                item_name: 'Cabana',
                item_brand: 'PoP',
                item_variant: '',
                price: parsePriceToFloat(unitPriceWithDiscount),
                discount: parsePriceToFloat(unitDiscount),
                quantity: quantity || 1,
            }
        }
    )

    return {
        event: 'productAddToCart',
        ecommerce: {
            currency: 'PLN',
            items: [
                ...spaEcommerceItems,
                ...busTicketsEcommerceItems,
                ...cabanasEcommerceItems,
            ],
        },
    }
}

export const calculateAccommodationAddToCartEvent = (
    accommodation?: AccommodationTicket
) => {
    if (!accommodation)
        return {
            event: 'productAddToCart',
            ecommerce: {
                currency: 'PLN',
                items: [],
            },
        }

    const { totalPrice, totalPriceWithoutDiscount, detail } = accommodation
    const { rooms } = detail
    const totalDiscount = (totalPriceWithoutDiscount || 0) - (totalPrice || 0)

    const roomsSeparator = ', '
    let roomsNames = ''

    rooms?.forEach((room) => {
        const { roomName, quantity } = room
        const currentRoomDescription = `${roomName} x${quantity}`
        roomsNames += roomsNames
            ? roomsSeparator + currentRoomDescription
            : currentRoomDescription
    })

    return {
        event: 'productAddToCart',
        ecommerce: {
            currency: 'PLN',
            items: [
                {
                    item_name: 'Accomodation',
                    item_brand: 'PoP',
                    item_variant: roomsNames,
                    price: parsePriceToFloat(totalPrice || 0),
                    discount: parsePriceToFloat(totalDiscount),
                    quantity: 1,
                },
            ],
        },
    }
}

export const calculateUnlimitedAddToCartEvent = (
    cards: Card[],
    cart?: CartSummaryDto // only for unlimited renew
) => {
    const { totalPrice: cartTotalPrice } = cart || {}
    const isUnlimitedRenew = !!cart

    const unlimitedEcommerceItems = cards.map(
        ({ name, unitPrice, totalPrice, detail }) => {
            const unitPriceWithDiscount = totalPrice || 0
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount
            const recalculatedName = isUnlimitedRenew
                ? getVariantNameForUnlimitedRenewal(name)
                : name
            const { person } = detail
            return {
                item_name: 'Unlimited',
                item_brand: 'PoP',
                item_variant: `${recalculatedName}, ${person}`,
                price: parsePriceToFloat(
                    cartTotalPrice ? cartTotalPrice : unitPriceWithDiscount
                ),
                discount: parsePriceToFloat(unitDiscount),
                quantity: 1,
            }
        }
    )

    return {
        event: 'productAddToCart',
        ecommerce: {
            currency: 'PLN',
            items: [...unlimitedEcommerceItems],
        },
    }
}

export const calculateVouchersAddToCartEvent = (
    vouchers: VoucherWithDetails[]
) => {
    const vouchersEcommerceItems = vouchers.map(
        ({ unitPrice, totalPrice, detail, name, description }) => {
            const unitPriceWithDiscount = totalPrice || 0
            const unitDiscount = (unitPrice || 0) - unitPriceWithDiscount
            const { amount, voucherType } = detail
            const isValueVoucher = voucherType === VOUCHER_TYPE_AMOUNT
            const valueVariant =
                !amount || ![100, 300, 500].includes(amount)
                    ? 'Custom'
                    : `${amount} PLN`
            const cardPerson = getPersonFromVoucherDescription(description)
            const cardVariant = `${name}, ${cardPerson}`

            return {
                item_name: 'Voucher',
                item_brand: 'PoP',
                item_variant: isValueVoucher ? valueVariant : cardVariant,
                price: parsePriceToFloat(unitPriceWithDiscount),
                discount: parsePriceToFloat(unitDiscount),
                quantity: 1,
            }
        }
    )

    return {
        event: 'productAddToCart',
        ecommerce: {
            currency: 'PLN',
            items: [...vouchersEcommerceItems],
        },
    }
}

export const calculateVouchersTypeEvent = (vouchers: VoucherWithDetails[]) => {
    const sampleVoucher = vouchers[0]
    if (!sampleVoucher) return {}

    const { detail } = sampleVoucher
    const areVouchersValueType = detail.voucherType === VOUCHER_TYPE_AMOUNT
    const voucherType = areVouchersValueType ? 'kwotowy' : 'unlimited'

    return {
        event: 'voucher_selected',
        voucher_type: voucherType,
    }
}
